import { useEffect, useRef, useState } from 'react'

import Axios from '../networking/intraceptor'
import * as path from '../networking/urlEndPoints'
import { authToken } from '../storage/authToken'

// App Mode is a pharmacy feature flag, not a module permission. Keep it in a
// small, shared request so every consumer sees the same current value.
let featureFlagsPromise = null
let featureFlagsToken = null
let featureFlagsVersion = 0
// When the current flags were requested. Returning to the page only re-fetches once they are this old,
// so switching tabs, focusing DevTools or clicking around no longer calls the API every time.
let featureFlagsRequestedAt = 0
const RETURN_REFRESH_AFTER_MS = 5 * 60 * 1000
const listeners = new Set()
let returnListenerCount = 0
let returnHandler = null

const refreshFeatureFlags = () => {
    featureFlagsPromise = null
    featureFlagsToken = null
    featureFlagsVersion += 1
    listeners.forEach((listener) => listener(featureFlagsVersion))
}

// App Mode only changes from the admin side, so a return to the page refreshes it only when the
// saved value is old. The focus and visibilitychange events fire together; the second one sees fresh data.
const refreshFeatureFlagsIfStale = () => {
    if (featureFlagsPromise && Date.now() - featureFlagsRequestedAt < RETURN_REFRESH_AFTER_MS) return
    refreshFeatureFlags()
}

const fetchFeatureFlags = (fallbackAppMode) => {
    const token = authToken()
    if (!token) return Promise.resolve({ appMode: fallbackAppMode })
    if (featureFlagsPromise && featureFlagsToken === token) return featureFlagsPromise

    featureFlagsToken = token
    featureFlagsRequestedAt = Date.now()
    featureFlagsPromise = Axios.get(path.pharmacy_user_feature_flags)
        .then((response) => {
            const data = response?.data?.data ?? response?.data
            return { appMode: data?.app_mode === true || data?.app_mode === 1 || data?.app_mode === '1' }
        })
        // Preserve the login value if an older backend has not deployed the
        // endpoint yet, so this feature addition never blocks existing users.
        .catch(() => ({ appMode: fallbackAppMode }))

    return featureFlagsPromise
}

const usePharmacyFeatureFlags = (loginAppMode) => {
    const fallbackAppMode = loginAppMode === true || loginAppMode === 1 || loginAppMode === '1'
    const token = authToken()
    const [state, setState] = useState({ appMode: fallbackAppMode, loading: true })
    const [version, setVersion] = useState(featureFlagsVersion)
    const mounted = useRef(true)

    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    useEffect(() => {
        const listener = (nextVersion) => setVersion(nextVersion)
        listeners.add(listener)
        return () => { listeners.delete(listener) }
    }, [])

    useEffect(() => {
        returnListenerCount += 1
        if (returnListenerCount === 1 && typeof window !== 'undefined' && typeof document !== 'undefined') {
            returnHandler = () => {
                if (document.visibilityState === 'visible') refreshFeatureFlagsIfStale()
            }
            window.addEventListener('focus', returnHandler)
            document.addEventListener('visibilitychange', returnHandler)
        }

        return () => {
            returnListenerCount -= 1
            if (returnListenerCount === 0 && returnHandler) {
                window.removeEventListener('focus', returnHandler)
                document.removeEventListener('visibilitychange', returnHandler)
                returnHandler = null
            }
        }
    }, [])

    useEffect(() => {
        let active = true
        fetchFeatureFlags(fallbackAppMode).then((nextState) => {
            if (active && mounted.current) setState({ ...nextState, loading: false })
        })
        return () => { active = false }
    }, [token, version, fallbackAppMode])

    return state
}

export default usePharmacyFeatureFlags
